import { Empty, Spin } from 'core/components/ui';
import type { BaseEntity } from 'core/base';
import noop from 'lodash/noop';
import { usePerformanceMark, Stage } from '@cabify/prom-react';
import { useMeasure } from 'react-use';
import { useCallback, useEffect, useRef } from 'react';

import type { ContextMenuProps } from '../ContextMenu';
import { ContextMenu } from '../ContextMenu';
import { StyledEmpty, StyledFinder } from './Finder.styles';
import type { TreeWithEntityProps, FinderProps, TreeNode, FinderTree } from './types';
import { defaultTitleRender } from './utils';
import { useSelectedKeys } from './hooks';

export function Finder<TEntity extends BaseEntity = BaseEntity>({
  contextMenuItems = [],
  onChangeTree = noop,
  titleRender = defaultTitleRender,
  treeData,
  activeKey,
  onChangeActiveKey = noop,
  allowSelect = () => true,
  multiple,
  onSelect,
  showIcon = true,
  draggable = true,
  blockNode = true,
  emptyText,
  loading,
  minHeight = 400,
  ...props
}: FinderProps<TEntity>) {
  const treeRef = useRef<FinderTree<TEntity>>();
  const holderRef = useRef<HTMLDivElement | null>(null);

  const [selectedKeys, _onSelect] = useSelectedKeys<TEntity>({
    onSelect,
    allowSelect,
    activeKey,
    onChangeActiveKey,
    multiple,
    tree: treeRef.current,
    holderRef,
  });
  const createMenuRef = useRef(noop);
  const [setMeasure, { height }] = useMeasure<HTMLDivElement>();

  const onContextMenuClick = useCallback<ContextMenuProps<TreeNode<TEntity>>['onClick']>(
    (key, node) => {
      onChangeTree(key, node, selectedKeys);
    },
    [onChangeTree, selectedKeys],
  );

  const onRightClick = useCallback<NonNullable<TreeWithEntityProps<TEntity>['onRightClick']>>(
    e => createMenuRef.current?.(e.node),
    [],
  );

  useEffect(() => {
    if (!activeKey) return;
    treeRef.current?.scrollTo({ key: activeKey });
  }, [activeKey]);

  const onDrop: TreeWithEntityProps<TEntity>['onDrop'] = info => {
    const { dragNode, dropPosition, dropToGap, node } = info;

    const getParentNode = () => {
      const isTargetParent = dragNode.parentNode?.key === node.key;

      if (dropToGap) {
        return node.parentNode;
      }

      if (isTargetParent) {
        return dragNode.parentNode;
      }

      return node;
    };

    const parentNode = getParentNode()!;

    const nodePos = node.pos.split('-').at(-1)!;
    const newOrder = dropPosition === +nodePos ? 0 : dropPosition;

    onChangeTree(JSON.stringify({ action: 'move' }), dragNode, parentNode, newOrder, selectedKeys);
  };

  const hasData = treeData && treeData.length > 0;

  usePerformanceMark(hasData ? Stage.Complete : Stage.Usable, 'finder');

  if (hasData) {
    return (
      <div
        ref={el => {
          if (el) setMeasure(el);
          holderRef.current = el;
        }}
        className="h-100"
      >
        <ContextMenu<TreeNode<TEntity>>
          createMenuRef={createMenuRef}
          onClick={onContextMenuClick}
          defaultItems={contextMenuItems}
        >
          <StyledFinder
            ref={treeRef}
            blockNode={blockNode}
            draggable={draggable}
            showIcon={showIcon}
            multiple={multiple}
            height={Math.max(minHeight, height)}
            titleRender={titleRender}
            treeData={treeData}
            onRightClick={onRightClick}
            onDrop={onDrop}
            selectedKeys={selectedKeys}
            onSelect={_onSelect}
            {...props}
          />
        </ContextMenu>
      </div>
    );
  }

  if (loading) {
    return <Spin spinning={loading} className="d-block" />;
  }

  return (
    <ContextMenu onClick={onContextMenuClick} defaultItems={contextMenuItems}>
      <StyledEmpty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
    </ContextMenu>
  );
}
